import { setDoc, doc, arrayUnion, writeBatch, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { CharacterCard, CardState, CharacterData } from '../types';
import { compressPixelArt, compressOriginalImage, compressOriginalImageSafe, compressImage, createDataUrl, detectImageFormat } from '../utils/imageCompression';
import { logger } from '../utils/secureLogger';
import { cleanCardDataForFirestore } from '../utils/dataUtils';
import { createInitialDescriptionMeta, createPrivateDiaryProvenance } from '../utils/descriptionMeta';

/**
 * カード保存の設定オプション
 */
export interface CardSaveOptions {
  /** 説明文を生成するかどうか */
  generateDescription?: boolean;
  /** 元画像を圧縮して保存するかどうか */
  saveOriginalImage?: boolean;
  /** 圧縮品質の設定 */
  compressionQuality?: {
    pixelArt?: number;     // ピクセルアートの品質 (デフォルト: 90)
    originalImage?: number; // 元画像の品質 (デフォルト: 80)
  };
}

/**
 * カード保存サービス
 * FeedPage、UserPageで重複していた画像圧縮・保存ロジックを統合
 */
export class CardSaveService {
  private static instance: CardSaveService;

  private constructor() {}

  /**
   * シングルトンインスタンスを取得
   */
  public static getInstance(): CardSaveService {
    if (!CardSaveService.instance) {
      CardSaveService.instance = new CardSaveService();
    }
    return CardSaveService.instance;
  }

  /**
   * カードをFirestoreに保存（全ての圧縮・保存ロジックを統合）
   * @param card - 保存するカード
   * @param pixelArtBase64 - ピクセルアートのBase64データ
   * @param options - 保存オプション
   */
  async saveCard(
    card: CharacterCard, 
    pixelArtBase64: string, 
    options: CardSaveOptions = {}
  ): Promise<CharacterCard> {
    if (!db) {
      throw new Error('Database not available');
    }

    if (!card.userId) {
      throw new Error('User must be signed in to save cards');
    }

    try {
      logger.info("Compressing images and saving to database...");

      // デフォルトオプションの設定
      const {
        generateDescription = true,
        saveOriginalImage = true
      } = options;

      // 圧縮前のサイズチェック（最小10KB = 約13,333文字のBase64）
      const MIN_IMAGE_SIZE = 13333; // 10KB相当のBase64文字数
      if (!pixelArtBase64 || pixelArtBase64.length < MIN_IMAGE_SIZE) {
        logger.error('Input image is too small or corrupted', {
          size: pixelArtBase64?.length || 0,
          sizeKB: Math.round((pixelArtBase64?.length || 0) * 0.75 / 1024),
          expected: `>= ${MIN_IMAGE_SIZE} chars (10KB)`
        });
        throw new Error('Image data is corrupted or incomplete (less than 10KB). Cannot save.');
      }

      // ピクセルアートを圧縮（100KB制限）
      const imageFormat = detectImageFormat(pixelArtBase64);

      // サーバーサイドで既に圧縮済みなので、クライアント側の圧縮はスキップ
      // Safari問題を回避するため、サーバーで圧縮している
      const compressedPixelArt = pixelArtBase64;  // そのまま使用

      // 以下、クライアント側圧縮は無効化（Safari問題のため）
      // const compressedPixelArt = await compressPixelArt(
      //   pixelArtBase64,
      //   imageFormat
      // );

      // 圧縮後のサイズチェック（削除 - 5KB以下でも許可）
      // 完全に空の場合のみエラーとする
      if (!compressedPixelArt || compressedPixelArt.length === 0) {
        logger.error('Compressed image is completely empty');
        throw new Error('Image compression failed completely. Cannot save.');
      }

      // 元の画像も圧縮（必要な場合）
      let compressedOriginalImage = '';
      if (saveOriginalImage && card.originalFile) {
        const originalImageBase64 = await this.fileToBase64(card.originalFile);
        const originalFormat = detectImageFormat(originalImageBase64);
        compressedOriginalImage = await compressOriginalImage(
          originalImageBase64,
          originalFormat
        );
      }

      // 圧縮後のサイズをログ出力
      const pixelArtSize = Math.round(compressedPixelArt.length * 0.75 / 1024);
      const originalImageSize = compressedOriginalImage ? 
        Math.round(compressedOriginalImage.length * 0.75 / 1024) : 0;
      
      logger.silent(`Pixel art: ${Math.round(pixelArtBase64.length * 0.75 / 1024)}KB → ${pixelArtSize}KB`);
      if (originalImageSize > 0) {
        logger.silent(`Original image: compressed to ${originalImageSize}KB`);
      }

      // 説明文の調整
      const characterDataToSave = generateDescription 
        ? card.characterData 
        : card.characterData ? { 
            ...card.characterData, 
            description: '' 
          } : null;

      // 日本語日記の出自メタデータ：AI生成の description_ja がある新規カードにのみ付与
      // （既に持っている場合はそれを尊重。無ければ原文を捏造しない）
      const descriptionMeta = card.descriptionMeta
        ?? createInitialDescriptionMeta(characterDataToSave?.description_ja);
      const privateDiaryProvenance = createPrivateDiaryProvenance(characterDataToSave?.description_ja);

      // 保存用カードデータの準備（元画像データは含めない）
      // generationMetadata / descriptionMeta が undefined の場合は cleanCardDataForFirestore で除去される
      const cardToSave = {
        id: card.id,
        userId: card.userId,
        characterData: characterDataToSave,
        exifData: card.exifData,
        pixelArtUrl: createDataUrl(compressedPixelArt),  // Imagen APIはJPEG形式で返す
        hasOriginalImage: !!compressedOriginalImage, // 元画像の有無のみ記録
        status: CardState.SUCCESS,
        error: null,
        generationMetadata: card.generationMetadata,
        descriptionMeta,
        createdAt: card.createdAt || new Date(),
        updatedAt: new Date()
      };

      // 段階的圧縮によるサイズ最適化（UserPageの重複ロジック統合）
      let totalDataSize = JSON.stringify(cardToSave).length;
      if (totalDataSize > 800000 && card.originalFile) { // 800KB制限
        logger.silent("Applying additional compression due to large data size");
        
        // 段階1: より強力な元画像圧縮
        const originalBase64 = await this.fileToBase64(card.originalFile);
        const originalFormat = detectImageFormat(originalBase64);
        compressedOriginalImage = await compressOriginalImageSafe(originalBase64, originalFormat);
        totalDataSize = JSON.stringify(cardToSave).length;

        if (totalDataSize > 900000) { // 900KB制限
          // 段階2: さらなる圧縮（150KB制限）
          logger.silent("Applying maximum compression (150KB limit)");
          compressedOriginalImage = await compressImage(originalBase64, 150, originalFormat);
        }
      }

      // Firestoreに保存（undefinedデータをクリーンアップ）
      logger.silent("Saving compressed card to database...");
      const cleanedCardData = cleanCardDataForFirestore(cardToSave);
      const saveBatch = writeBatch(db);
      saveBatch.set(doc(db, "cards", cardToSave.id), cleanedCardData);
      if (privateDiaryProvenance) {
        saveBatch.set(
          doc(db, "users_private", card.userId, "cardMetadata", cardToSave.id),
          { ...privateDiaryProvenance, createdAt: serverTimestamp() },
        );
      }
      await saveBatch.commit();
      
      // 月一覧ドキュメントを更新
      const cardDate = card.exifData?.createDate || card.createdAt || new Date();
      const d = cardDate instanceof Date ? cardDate : new Date(cardDate);
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      try {
        await setDoc(doc(db, "meta", "months"), { months: arrayUnion(monthKey) }, { merge: true });
      } catch (e) {
        // 月一覧更新失敗は致命的ではない
      }

      logger.info("Card saved to database successfully!");

      return {
        ...card,
        ...cardToSave,
        pixelArtUrl: createDataUrl(compressedPixelArt),
        originalImageUrl: undefined // クライアントサイドには元画像データを返さない
      };

    } catch (error) {
      logger.error("Error saving card:", error);
      throw new Error(`Failed to save card: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * カードを削除（UserPageの重複ロジックを統合）
   * @param cardId - 削除するカードID
   * @param card - カード情報（状態確認用）
   * @param userId - ユーザーID（権限確認用）
   */
  async deleteCard(cardId: string, card: CharacterCard | null, userId: string): Promise<void> {
    if (!db) {
      throw new Error('Database not available');
    }

    if (card?.userId !== userId) {
      throw new Error('Cannot delete other users cards');
    }

    try {
      // Firestoreに保存されているカードの場合のみFirestoreから削除
      if (card && card.status === CardState.SUCCESS) {
        const deleteBatch = writeBatch(db);
        deleteBatch.delete(doc(db, "cards", cardId));
        deleteBatch.delete(doc(db, "users_private", userId, "cardMetadata", cardId));
        await deleteBatch.commit();
        logger.info(`Card deleted from database: ${cardId}`);
      } else {
        logger.info(`Card removed from local state only: ${cardId}`);
      }
    } catch (error) {
      logger.error("Error deleting card:", error);
      throw new Error('Failed to delete card');
    }
  }

  /**
   * ファイルをBase64に変換する内部メソッド
   */
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        resolve(dataUrl.split(',')[1]);
      };
      reader.readAsDataURL(file);
    });
  }

  /**
   * 圧縮品質を動的に調整
   * @param dataSize - データサイズ（バイト）
   * @param maxSize - 最大サイズ（バイト）
   * @returns 推奨される圧縮品質（0-100）
   */
}

// シングルトンインスタンスをエクスポート
export const cardSaveService = CardSaveService.getInstance();